import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import Icon from "@ui-kit/icons/Icon.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Button from "@ui-kit/form/Button/Button.web";
import useRedirect from "@shared/hooks/useRedirect";
import url from "@shared/utils/urls/url";
import { Timer } from "@ui-kit/feedback/timer/Timer";
import { useDisclosure } from "@shared/hooks/useDisclosure";
import { useRouter } from "next/router";
import { useCallback } from "react";
import CartSummaryModal from "@app-contexts/public/features/basket/components/CartSummaryModal/CartSummaryModal.web";
import { useTranslation } from "@shared/utils/internationalization";
import { NavBarCartDisplayProps } from "./NavBarCartDisplay.props";

const NavBarCartDisplay = ({ isDetailPage }: NavBarCartDisplayProps) => {
  const { t } = useTranslation("common");
  const router = useRouter();
  const { hasItems, expiryDate, isBasketUpdating, totalItemQuantity } =
    usePublicBasket();

  const holdLink = router.query.link as string;

  const { redirect } = useRedirect();
  const {
    isOpen: isCartSummaryModalOpen,
    onOpen: openCartSummaryModal,
    onClose,
  } = useDisclosure();

  const onClick = useCallback(() => {
    // TODO: if we want the cart summary modal to open outside of the detail pages,
    // we need to set up a seatingstateprovider for this component
    if (isDetailPage) {
      openCartSummaryModal();
    } else {
      // TODO: Remove asWindow when checkout exists in NextJS
      redirect(url("checkout/purchase/"), { asWindow: true });
    }
  }, [redirect, openCartSummaryModal, isDetailPage]);

  const actionText = isDetailPage
    ? t("Open Cart Summary Modal")
    : t("Go to Checkout");

  // Create the descriptive label
  const cartLabel = t("Cart button with {{count}} items, Action: {{action}}", {
    count: totalItemQuantity,
    action: actionText,
  });

  return (
    <>
      <CartSummaryModal
        isOpen={isCartSummaryModalOpen}
        onClose={onClose}
        venue={undefined}
        holdLink={holdLink}
      />
      {hasItems && expiryDate ? (
        <Button.Nav
          data-testid="navbar-cart-display-button"
          aria-label={cartLabel}
          isLoading={isBasketUpdating}
          onClick={onClick}
          leftIcon={
            <Icon.Cart.Outline boxSize="element.lg" color="secondary.600" />
          }
        >
          <span>({totalItemQuantity})</span>
          <Box w="element.2xs" />
          <Timer
            targetDate={expiryDate}
            disableLiveAnnouncements={isCartSummaryModalOpen}
            announcementSuffix={t("left to complete transaction!")}
          />
        </Button.Nav>
      ) : null}
    </>
  );
};

export default NavBarCartDisplay;
